"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { ELLIPSIS, getPageItems } from "../utils/pagination";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
};

/**
 * Client-side pager for the public web grids (teachers, a teacher's courses).
 *
 * Which page numbers to show is decided by `getPageItems` in
 * `../utils/pagination`, which collapses long ranges behind ellipses so a
 * 40-page list stays the same width as a 3-page one.
 *
 * Chevrons are mirrored under `rtl:` because "previous" points at the start of
 * the reading direction, not at the left edge of the screen.
 */
export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
}: PaginationProps) {
  // Before the early return: hooks cannot run conditionally.
  const t = useTranslations("web.pagination");

  if (totalPages <= 1) {
    return null;
  }

  const pages = getPageItems(currentPage, totalPages);

  const buttonBase =
    "flex h-10 min-w-10 cursor-pointer items-center justify-center rounded-[10px] px-3 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <nav
      aria-label={t("label")}
      className={clsx("flex items-center justify-center gap-2", className)}
    >
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        aria-label={t("previous")}
        className={clsx(
          buttonBase,
          "border border-slate-200 text-primary hover:border-secondary hover:text-secondary disabled:hover:border-slate-200 disabled:hover:text-primary",
        )}
      >
        <ChevronLeft size={18} className="rtl:rotate-180" />
      </button>

      {pages.map((page, index) =>
        page === ELLIPSIS ? (
          <span
            key={`gap-${index}`}
            aria-hidden="true"
            className="px-1 text-sm font-bold text-slate-400"
          >
            &hellip;
          </span>
        ) : (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            aria-current={page === currentPage ? "page" : undefined}
            className={clsx(
              buttonBase,
              page === currentPage
                ? "bg-primary text-white"
                : "border border-slate-200 text-primary hover:border-secondary hover:text-secondary",
            )}
          >
            {page}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        aria-label={t("next")}
        className={clsx(
          buttonBase,
          "border border-slate-200 text-primary hover:border-secondary hover:text-secondary disabled:hover:border-slate-200 disabled:hover:text-primary",
        )}
      >
        <ChevronRight size={18} className="rtl:rotate-180" />
      </button>
    </nav>
  );
}
